import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type { SiteStatsCollection } from '@freelancer/datastore/collections';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { FontColor, TextAlign, TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-marketplace-stats',
  template: `
    <div
      class="MarketplaceStats"
      [class.MarketplaceStats--dark]="darkMode"
    >
      <div class="MarketplaceStats-wrapper">
        <fl-heading
          class="MarketplaceStats-header"
          i18n="Marketplace Statistics header"
          [color]="darkMode ? HeadingColor.LIGHT : HeadingColor.FOREGROUND"
          [size]="TextSize.XLARGE"
          [headingType]="HeadingType.H2"
          [flMarginBottom]="'mid'"
        >
          The World's Largest Marketplace
        </fl-heading>

        <fl-text
          class="MarketplaceStats-description"
          i18n="Marketplace statistics description"
          [color]="darkMode ? FontColor.LIGHT : FontColor.FOREGROUND"
          [textAlign]="TextAlign.CENTER"
          [size]="TextSize.LARGE"
          [flMarginBottom]="'xxlarge'"
        >
          Millions of users, from small businesses to large enterprises,
          entrepreneurs to startups, use Freelancer to turn their ideas into
          reality.
        </fl-text>
        <div class="MarketplaceStats-info-container">
          <div
            class="MarketplaceStats-info MarketplaceStats-user"
            [flMarginBottom]="'small'"
            [flMarginBottomDesktop]="'none'"
          >
            <p
              class="text-color-inherit text-size-inherit MarketplaceStats-info-number text-center"
              i18n="Number of users (in millions) on the site"
            >
              {{ userCount$ | flAsync }}M
            </p>
            <fl-text
              class="MarketplaceStats-info-label"
              i18n="Number of users on the site header"
              [color]="darkMode ? FontColor.LIGHT : FontColor.FOREGROUND"
              [textAlign]="TextAlign.CENTER"
              [size]="TextSize.LARGE"
            >
              Registered Users
            </fl-text>
          </div>
          <div class="MarketplaceStats-info">
            <p
              class="text-color-inherit text-size-inherit MarketplaceStats-info-number text-center"
              i18n="Number of projects (in millions) posted on the site"
            >
              {{ projectCount$ | flAsync }}M
            </p>
            <fl-text
              class="MarketplaceStats-info-label"
              i18n="Number of jobs posted on the site header"
              [color]="darkMode ? FontColor.LIGHT : FontColor.FOREGROUND"
              [textAlign]="TextAlign.CENTER"
              [size]="TextSize.LARGE"
            >
              Total Jobs Posted
            </fl-text>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./marketplace-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketplaceStatsComponent implements OnInit {
  FontColor = FontColor;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  TextAlign = TextAlign;
  TextSize = TextSize;

  userCount$: Observable<string>;
  projectCount$: Observable<string>;

  @Input() darkMode = false;

  constructor(private datastore: Datastore) {}

  ngOnInit(): void {
    const siteStats$ = this.datastore
      .document<SiteStatsCollection>('siteStats')
      .valueChanges();

    this.userCount$ = siteStats$.pipe(
      map(stat => (stat.userCount / 1_000_000).toFixed(1)),
    );

    this.projectCount$ = siteStats$.pipe(
      map(stat => (stat.projectCount / 1_000_000).toFixed(1)),
    );
  }
}
